/*
 * <pack-selector> — the "choose your pack" tier chooser.
 *
 * The tiers are buttons in a radiogroup rather than real radio inputs: they
 * carry rich content (badge, sock icons, perk pills, two prices) that a styled
 * <input type="radio"> label makes awkward to lay out, and the group has to be
 * keyboard-navigable either way. Roving tabindex + arrow keys gives the same
 * semantics a native radiogroup would.
 *
 * Choosing a tier has to end up selecting a product variant. Two ways, and the
 * difference matters:
 *
 *   option mode  (data-option-name + per-tier data-option-value)
 *     Clicks the theme's own variant picker for that option. Everything
 *     downstream — price, availability, media, history, the Intelligems price
 *     hooks — is the theme's normal variant flow, untouched. Other options
 *     (colour) keep whatever the shopper picked. This is the real mode.
 *
 *   pinned mode  (per-tier data-variant-id, no option name)
 *     Writes the variant id straight into the product form. Use only while the
 *     pack dimension does not exist on the product yet: it pins one variant, so
 *     it will override a colour choice. The snippet says as much in the editor.
 *
 * The notice bar under the tiers has three states. "nudge" is the one that
 * earns its keep — on the single pair it offers the upgrade in one tap instead
 * of making the shopper go back up and re-pick.
 */
class PackSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return
    this.initialised = true

    this.tiers = Array.from(this.querySelectorAll('[data-pack-tier]'))
    if (!this.tiers.length) return

    this.notice = this.querySelector('[data-pack-notice-bar]')
    this.noticeText = this.querySelector('[data-pack-notice-text]')
    this.noticeIcon = this.querySelector('[data-pack-notice-icon]')
    this.upgrade = this.querySelector('[data-pack-upgrade]')
    this.optionName = this.dataset.optionName || ''

    this.tiers.forEach((tier) => {
      tier.addEventListener('click', () => this.select(tier.dataset.packTier))
      tier.addEventListener('keydown', (event) => this.onKeydown(event))
    })

    if (this.upgrade) {
      this.upgrade.addEventListener('click', () => {
        const target = this.upgrade.dataset.packUpgrade
        if (target) this.select(target)
      })
    }

    // A default tier whose variant is sold out would open the page on a choice
    // nobody can take, so availability wins over the configured default.
    const initial =
      this.tiers.find((tier) => tier.dataset.packDefault === 'true' && !tier.disabled) ||
      this.tiers.find((tier) => !tier.disabled) ||
      this.tiers[0]

    // The first paint only reflects the markup's own selected state — syncing
    // the variant on load would fight a ?variant= in the URL, which is how
    // every email and ad link arrives.
    this.select(initial.dataset.packTier, {syncVariant: false})

    this.onVariantChange = this.onVariantChange.bind(this)
    this.onOptionChange = this.onOptionChange.bind(this)
    this.watchVariantPrice()
    this.watchSockColor()
    this.syncSockColor()
    // A slow picker can outlast the retry window on a cold cache.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.applySockColor(), {once: true})
    }
  }

  watchSockColor() {
    this.optionPicker = document.querySelector('[data-variant-picker]')
    if (this.optionPicker) this.optionPicker.addEventListener('change', this.onOptionChange)
  }

  onOptionChange() {
    // The picker swaps the checked radio during its own change handling, so
    // read the swatch once that has settled.
    requestAnimationFrame(() => this.applySockColor())
  }

  disconnectedCallback() {
    if (this.variantObserver) this.variantObserver.disconnect()
    if (this.variantInput) this.variantInput.removeEventListener('change', this.onVariantChange)
    if (this.optionPicker) this.optionPicker.removeEventListener('change', this.onOptionChange)
  }

  get selectedTier() {
    return this.tiers.find((tier) => tier.classList.contains('is-selected'))
  }

  select(id, {syncVariant = true} = {}) {
    const tier = this.tiers.find((candidate) => candidate.dataset.packTier === id)
    if (!tier || tier.disabled) return

    this.tiers.forEach((candidate) => {
      const on = candidate === tier
      candidate.classList.toggle('is-selected', on)
      candidate.setAttribute('aria-checked', on ? 'true' : 'false')
      // Roving tabindex: one stop for the whole group, arrows move within it.
      candidate.tabIndex = on ? 0 : -1
    })

    this.renderNotice(tier)
    this.renderPrice(tier)
    this.syncQuantity(tier)
    if (syncVariant) this.syncVariant(tier)
  }

  onKeydown(event) {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft']
    if (!keys.includes(event.key)) return
    event.preventDefault()

    const selectable = this.tiers.filter((tier) => !tier.disabled)
    const current = selectable.indexOf(this.selectedTier)
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight'
    const next = selectable[(current + (forward ? 1 : -1) + selectable.length) % selectable.length]

    if (next) {
      this.select(next.dataset.packTier)
      next.focus()
    }
  }

  renderNotice(tier) {
    if (!this.notice) return

    const html = tier.dataset.packNotice || ''
    const state = tier.dataset.packNoticeState || 'info'

    this.notice.hidden = html === ''
    this.notice.dataset.state = state
    if (this.noticeText) this.noticeText.innerHTML = html
    if (this.noticeIcon) this.noticeIcon.textContent = state === 'nudge' ? '!' : '✓'

    if (this.upgrade) {
      const target = tier.dataset.packUpgradeTo
      const label = tier.dataset.packUpgradeLabel
      const targetExists = target && this.tiers.some((candidate) => candidate.dataset.packTier === target)

      this.upgrade.hidden = !targetExists
      if (targetExists) {
        this.upgrade.dataset.packUpgrade = target
        if (label) this.upgrade.textContent = label
      }
    }
  }

  /* Mirrors the tier price anywhere the page asks for it — typically the
     add-to-cart label, which reads better as "Add to cart · $39.99". */
  renderPrice(tier) {
    const price = tier.dataset.packPrice
    if (!price) return

    document.querySelectorAll('[data-pack-price-target]').forEach((target) => {
      target.textContent = price
    })
  }

  /* The sock graphics wear the colour the shopper picked. The value is read
     off the variant picker's own swatch rather than a mapping kept in here, so
     the socks and the swatches can never disagree. */
  syncSockColor(attempt = 0) {
    // The colour field is a custom element that builds its swatches itself, so
    // on first paint it may not have rendered yet. Retry for a few frames
    // rather than leaving the socks in the fallback colour.
    if (this.applySockColor()) return
    if (attempt < 30) requestAnimationFrame(() => this.syncSockColor(attempt + 1))
  }

  applySockColor() {
    const picker = document.querySelector('[data-variant-picker]')
    if (!picker) return false

    const field = Array.from(picker.querySelectorAll('[data-option-name]')).find((node) =>
      /colou?r/i.test(node.dataset.optionName || '')
    )
    // Absent could mean no colour option, or the field simply has not rendered
    // yet. Indistinguishable here, so report failure and let the retry cap end
    // it for products that genuinely have none.
    if (!field) return false

    const checked = field.querySelector('input[type="radio"]:checked')
    const node = checked && checked.closest('[data-value]')
    const swatch = node && node.querySelector('label')
    if (!swatch) return false

    const fill = getComputedStyle(swatch).backgroundColor
    const channels = fill.match(/[\d.]+/g)
    // An image swatch paints with background-image and leaves this transparent;
    // the stylesheet's default is better than a see-through sock.
    if (!channels || channels.length < 3 || Number(channels[3]) === 0) return true

    const [r, g, b] = channels.slice(0, 3).map(Number)
    const light = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6

    this.style.setProperty('--m-pack-sock-fill', fill)
    // A pale sock on a pale card needs an outline, and its cuff stripe has to
    // flip to dark or it disappears.
    this.style.setProperty('--m-pack-sock-edge', light ? 'rgba(0, 0, 0, 0.35)' : 'transparent')
    this.style.setProperty('--m-pack-sock-cuff', light ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.35)')
    return true
  }

  get quantityMode() {
    return this.hasAttribute('data-quantity-mode')
  }

  get cartQuantityInput() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]')
    if (!form) return null

    // The theme renders the quantity field outside the form and ties it back
    // with a form attribute, so a descendant lookup misses it entirely.
    const owned = Array.from(document.querySelectorAll('input[name="quantity"]')).find(
      (input) => input.form === form
    )
    if (owned) return owned

    // Nothing to reuse on this template - give the form its own field.
    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = 'quantity'
    hidden.value = '1'
    form.appendChild(hidden)
    return hidden
  }

  /* A tier stands for how many of the chosen variant go in the cart, so the
     shopper's colour and size survive and the product needs no pack
     dimension. */
  syncQuantity(tier) {
    if (!this.quantityMode) return

    const quantity = parseInt(tier.dataset.packQty, 10)
    const input = this.cartQuantityInput
    if (!input || !Number.isFinite(quantity) || quantity < 1) return
    if (input.value === String(quantity)) return

    input.value = String(quantity)
    input.dispatchEvent(new Event('change', {bubbles: true}))
  }

  /* Totals are the live variant price times the quantity. Recalculated on
     every variant change so the page cannot advertise a total the cart will
     not charge. */
  watchVariantPrice() {
    if (!this.quantityMode && !this.optionName) return

    const form = document.querySelector('form[data-type="add-to-cart-form"]')
    const input = form && form.querySelector('input[name="id"]')
    if (!input) return

    this.variantInput = input
    this.lastVariantId = input.value
    this.variantObserver = new MutationObserver(() => this.onVariantChange())
    this.variantObserver.observe(input, {attributes: true, attributeFilter: ['value']})
    input.addEventListener('change', this.onVariantChange)
  }

  onVariantChange() {
    const id = this.variantInput && this.variantInput.value
    if (!id || id === this.lastVariantId) return
    this.lastVariantId = id
    this.applySockColor()
    this.renderOptionTotals(id)

    const variant = (window.MinimogProductVariants || []).find(
      (candidate) => String(candidate.id) === String(id)
    )
    if (variant) return this.renderTotals(variant.price, variant.compare_at_price)

    // No preloaded variant list on this template — ask the storefront.
    fetch(`${window.location.pathname}.js`)
      .then((response) => response.json())
      .then((product) => {
        const match = product.variants.find((candidate) => String(candidate.id) === String(id))
        if (match) this.renderTotals(match.price, match.compare_at_price)
      })
      .catch(() => {})
  }

  /* Driving a real option rather than a quantity: reprice each tier against the
     variant that matches the shopper's current gender and colour with only the
     pack position swapped. The variant list is the theme's own, so there is no
     second copy of the catalogue to keep in step. */
  get variantData() {
    if (this._variantData) return this._variantData
    const picker = document.querySelector('[data-variant-picker]')
    const json = picker && picker.querySelector('#productVariants[type="application/json"]')
    if (!json) return null
    try {
      this._variantData = JSON.parse(json.textContent)
    } catch (error) {
      this._variantData = null
    }
    return this._variantData
  }

  renderOptionTotals(currentId) {
    const position = parseInt(this.dataset.optionPosition, 10)
    if (!this.optionName || !Number.isFinite(position) || position < 1) return

    const variants = this.variantData
    if (!variants) return

    const current = variants.find((variant) => String(variant.id) === String(currentId))
    if (!current) return

    const format = window.MinimogSettings && window.MinimogSettings.money_format
    if (typeof window.formatMoney !== 'function' || !format) return

    this.tiers.forEach((tier) => {
      const value = tier.dataset.packOptionValue
      if (!value) return

      const wanted = [current.option1, current.option2, current.option3]
      wanted[position - 1] = value
      const match = variants.find(
        (variant) =>
          variant.option1 === wanted[0] &&
          variant.option2 === wanted[1] &&
          variant.option3 === wanted[2]
      )
      if (!match) return

      const total = tier.querySelector('[data-pack-total]')
      const totalCompare = tier.querySelector('[data-pack-total-compare]')
      const compare = match.compare_at_price > match.price ? match.compare_at_price : 0

      if (total) {
        total.textContent = window.formatMoney(match.price, format)
        tier.dataset.packPrice = total.textContent
      }
      if (totalCompare) {
        totalCompare.textContent = compare ? window.formatMoney(compare, format) : ''
        totalCompare.hidden = !compare
      }
      if (unitPrice) {
        // The per-unit figure is the pack's own price over how many are in it,
        // so it has to follow the variant the same way the total does.
        const pairs = parseInt(tier.dataset.packPairs, 10)
        if (Number.isFinite(pairs) && pairs > 0) {
          unitPrice.textContent = window.formatMoney(Math.round(match.price / pairs), format)
        }
      }
      tier.disabled = !match.available
    })

    // Changing colour or gender can sell out the tier that was selected.
    const selected = this.selectedTier
    if (selected && selected.disabled) {
      const fallback = this.tiers.find((tier) => !tier.disabled)
      if (fallback) return this.select(fallback.dataset.packTier)
    }
    if (selected) this.renderPrice(selected)
  }

  renderTotals(price, compareAt) {
    const format = window.MinimogSettings && window.MinimogSettings.money_format
    if (typeof window.formatMoney !== 'function' || !format) return

    const compare = compareAt > price ? compareAt : 0
    this.dataset.unitPrice = String(price)
    this.dataset.unitCompare = String(compare)

    this.tiers.forEach((tier) => {
      const quantity = parseInt(tier.dataset.packQty, 10) || 1
      const total = tier.querySelector('[data-pack-total]')
      const totalCompare = tier.querySelector('[data-pack-total-compare]')

      if (total) {
        total.textContent = window.formatMoney(price * quantity, format)
        tier.dataset.packPrice = total.textContent
      }
      if (totalCompare) {
        totalCompare.textContent = compare ? window.formatMoney(compare * quantity, format) : ''
        totalCompare.hidden = !compare
      }
    })

    const selected = this.selectedTier
    if (selected) this.renderPrice(selected)
  }

  syncVariant(tier) {
    if (this.optionName) {
      this.selectOptionValue(tier.dataset.packOptionValue)
    } else if (tier.dataset.packVariantId) {
      this.setVariantId(tier.dataset.packVariantId)
    }
  }

  /* Drive the theme's own picker so its variant logic runs. The picker renders
     as radios or as a <select> depending on how the option is configured, so
     handle both rather than assuming. */
  selectOptionValue(value) {
    if (!value) return

    const picker = document.querySelector('[data-variant-picker]')
    if (!picker) return

    const field = picker.querySelector(`[data-option-name="${CSS.escape(this.optionName)}"]`)
    if (!field) return

    const select = field.querySelector('select')
    if (select) {
      if (select.value === value) return
      select.value = value
      select.dispatchEvent(new Event('change', {bubbles: true}))
      return
    }

    const node = field.querySelector(`[data-value="${CSS.escape(value)}"]`)
    const input = node && node.querySelector('input[type="radio"]')
    if (input && !input.checked) input.click()
  }

  setVariantId(variantId) {
    const form = document.querySelector('form[data-type="add-to-cart-form"]')
    const input = form && form.querySelector('input[name="id"]')
    if (!input) return

    input.value = variantId
    input.dispatchEvent(new Event('change', {bubbles: true}))
  }
}

if (!customElements.get('pack-selector')) {
  customElements.define('pack-selector', PackSelector)
}
